import React, {Component} from 'react';
import {bindActionCreators, Dispatch} from 'redux';
import {connect} from 'react-redux';
import {throttle} from 'lodash';

import {Divider} from 'primereact/divider';

import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item from '~/shared/src/ci/Item';
import {getTargetArea} from '~/shared/src/utilities/modules/finder';
import {
    CheckItemKeyUniquenessParameters,
    CheckItemKeyUniquenessResult
} from '~/shared/src/utilities/modules/data';
import objectUtils from '~/shared/src/utilities/objectUtils';

import StatusDropdown from '~/frontend/src/components/base/shared/field/StatusDropdown';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';

import {StoreState} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {
    getItemKeyDisplayName,
    getItemNameDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {checkKeyUniqueFeOnly} from '~/frontend/src/utilities/dataUtils';

import * as DataActions from '~/frontend/src/actions/data';
import {UpdateItem} from '~/frontend/src/actions/data/updateItem';
import * as ElusiveDataActions from '~/frontend/src/actions/elusiveData';
import {SetElusiveDataAction, UnsetElusiveDataAction} from '~/frontend/src/actions/elusiveData';
import {timestampRenderer} from '~/shared/src/utilities/valueRenderers';


const Actions = Object.assign( {}, DataActions, ElusiveDataActions ) as any;
type SetElusiveData = ( panelKey : string, data : PanelElusiveData ) => SetElusiveDataAction;
type UnsetElusiveData = ( panelKey : string, keys : ( keyof PanelElusiveData )[] ) => UnsetElusiveDataAction;

const defaultProps = {
    ...defaultBaseRequestParameters,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    /**
     * Is data in this form editable at all?
     */
    editable: false as boolean,

    /**
     * Additional context to be checked against (key unique, reference cardinality)
     */
    additionalCheckDataContexts: [] as string[]
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The item.
     *
     * Will be mapped from global state.
     */
    item : Item;

    /**
     * Function invoked if form should render a new item (rootItemID < 0) and no global.data therefor existing.
     */
    onErrorDataChanged? : ( uid : string ) => void;

};

type PanelElusiveData = PanelErrorData & {
    key? : string;
    keyError? : string;
    name? : string;
    nameError? : string;
};

type StateProps = {

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;
};

type ActionProps = {
    // data actions
    updateItem : UpdateItem;

    // elusiveData actions
    setElusiveData : SetElusiveData;
    unsetElusiveData : UnsetElusiveData;
}

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;

const historyStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'history' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'history' )?.id
};

const deletedStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'deleted' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'deleted' )?.id
};

export const resetStatusIdCaches = () => {
    historyStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'history' )?.id;
    historyStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'history' )?.id;
    deletedStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'deleted' )?.id;
    deletedStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'deleted' )?.id;
};


/**
 * AttributesForm
 */
class AttributesForm extends Component<AllProps> {

    static defaultProps = defaultProps;

    /**
     * REDUX stuff
     */
    constructor( props : AllProps ) {
        super( props );

        this.checkKeyUniqueness = throttle( this.checkKeyUniqueness, 200, { trailing: true } );

        this.onKeyChange = this.onKeyChange.bind( this );
        this.onNameChange = this.onNameChange.bind( this );
        this.onStatusDropdownChange = this.onStatusDropdownChange.bind( this );

        // initial check
        this.onKeyChange( props.item.key, undefined, true );
        this.onNameChange( props.item.name );
    }

    componentDidMount() {
        this.props.setElusiveData( this.props.uid, {
            errorData: {
                count  : 0,
                tooltip: [],
                message: []
            }
        } );
    }

    /**
     * REDUX stuff
     */
    shouldComponentUpdate( nextProps : AllProps ) : boolean {
        const changedProps = objectUtils.getDiffKeys( this.props, nextProps, [], [ 'elusiveData' ] );

        let errorData = nextProps.elusiveData?.errorData ?? {
            count  : 0,
            tooltip: [],
            message: []
        };

        if ( changedProps.includes( 'elusiveData' ) || changedProps.includes( 'elusiveData.keyError' ) || changedProps.includes( 'elusiveData.nameError' ) ) {
            errorData = {
                count  : 0,
                tooltip: [],
                message: []
            };

            if ( nextProps.elusiveData?.keyError ) {
                errorData.count!++;

                errorData.tooltip!.push( nextProps.elusiveData.keyError );

                errorData.message!.push( <div className="yn-error-message">{nextProps.elusiveData.keyError}</div> );
            }
            if ( nextProps.elusiveData?.nameError ) {
                errorData.count!++;

                if ( errorData.tooltip!.length ) {
                    errorData.tooltip!.push( <br/> );
                }
                errorData.tooltip!.push( nextProps.elusiveData.nameError );

                if ( errorData.message!.length ) {
                    errorData.message!.push( <hr className="yn-error-ruler"/> );
                }
                errorData.message!.push( <div className="yn-error-message">{nextProps.elusiveData?.nameError}</div> );
            }
        }

        if ( nextProps.elusiveData?.errorData !== errorData ) {
            this.props.setElusiveData( nextProps.uid, { errorData } );
            this.props.onErrorDataChanged?.( nextProps.uid );
        }

        return true;
    }

    /**
     * Checks the given key for uniqueness within the given ItemType against the backend.
     *
     * @private
     */
    checkKeyUniqueness( key : string, forceCheck : boolean = false ) : boolean | undefined {
        const {
                  uid,
                  dataContext,
                  additionalCheckDataContexts,
                  item
              } = this.props;

        let retVal : boolean = true;

        if ( item && key ) {
            // no check needed if key was checked (item.key) or is original one (item.attributesModified.get( 'key' ))
            if ( forceCheck || ( key !== item.key && key !== item.attributesModified.get( 'key' ) ) ) {
                const componentUid      = `${uid}_checkIKU`, // own componentUid to avoid showing loading mask on form
                      requestUid        = `checkKeyUniqueness`,
                      checkDataContexts = ( [ dataContext ] as string[] ).concat( additionalCheckDataContexts ),
                      endUpCheckFnc     = ( isKeyUnique : boolean ) => {
                          if ( !isKeyUnique ) {
                              this.props.setElusiveData( uid, {
                                  key,
                                  keyError: __( 'YN.base.finder.item.key.error.notUnique' )
                              } );
                          }
                          else {
                              this.props.unsetElusiveData( uid, [ 'keyError' ] );
                              this.props.updateItem( dataContext, item.id, { key } );
                          }
                      };

                let keyIsUnique : boolean = true;

                // abort running check
                xhr.abort( componentUid, requestUid );

                for ( const checkDataContext of checkDataContexts ) {
                    if ( !checkKeyUniqueFeOnly( checkDataContext, item.itemType.id, key, item.id ).keyIsUnique ) {
                        keyIsUnique = false;
                        break;
                    }
                }

                // check key uniqueness against frontend data
                if ( !keyIsUnique ) {
                    const delayFnc = async () => {
                        await Promise.resolve();
                        endUpCheckFnc( false );
                    };

                    void delayFnc();
                }

                // check uniqueness against database
                else {
                    const doLoad = async () => {
                        const params : CheckItemKeyUniquenessParameters = {
                                  schema  : this.props.schema,
                                  status  : 'all',
                                  itemType: item.itemType.id,
                                  key
                              },
                              response                                  = await xhr.post(
                                  `${BASE_URL}/api/data/checkItemKeyUniqueness`,
                                  params,
                                  undefined,
                                  componentUid,
                                  requestUid
                              );

                        if ( response?.data ) {
                            const responseData = response.data as CheckItemKeyUniquenessResult;

                            if ( responseData.success ) {
                                keyIsUnique = responseData.data.keyIsUnique;

                                // check database item having the key within frontend data (item could be deleted or key changed)
                                if ( !responseData.data.keyIsUnique ) {
                                    keyIsUnique = true;

                                    for ( const checkDataContext of checkDataContexts ) {
                                        const cItem = global.data[ checkDataContext ]?.items[ responseData.data.itemId ];

                                        if ( !cItem || ( cItem.key === key && cItem.status.name !== 'deleted' ) ) {
                                            keyIsUnique = false;
                                            break;
                                        }
                                    }
                                }

                                endUpCheckFnc( keyIsUnique );
                            }
                        }
                    };

                    doLoad().catch( ( error ) => {
                        console.log( error );
                    } );
                }
            }

            // no check needed -> no error, set key if
            else {
                retVal = false;

                this.props.unsetElusiveData( this.props.uid, [ 'keyError' ] );

                if ( key !== item.key ) {
                    this.props.updateItem( dataContext, item.id, { key } );
                }
            }
        }

        return retVal;
    }

    /**
     * Handler for attribute key StringField change
     *
     * @private
     */
    onKeyChange( value : string | undefined, event? : any, doNotCheckKeyUniqueness : boolean = false ) {
        const isAutogenerated = this.props.item.itemType?.isKeyAutogenerated ?? false,
              key = value;

        let keyError = '';

        if ( !isAutogenerated ) {
            if ( !key ) {
                keyError = __( 'YN.base.finder.item.key.error.empty' );
            }
            else if ( / /.test( key ) ) {
                keyError = __( 'YN.base.finder.item.key.error.whiteSpaces' );
            }
        }

        if ( keyError ) {
            this.props.setElusiveData( this.props.uid, { key, keyError } );
        }
        else {
            this.props.unsetElusiveData( this.props.uid, [ 'key', 'keyError' ] );
        }

        if ( !isAutogenerated && !keyError && key && !doNotCheckKeyUniqueness && this.checkKeyUniqueness( key ) ) {
            keyError = __( 'YN.base.finder.item.key.check.unique' );
            this.props.setElusiveData( this.props.uid, { key, keyError } );
        }
    }

    /**
     * Handler for attribute name StringField change
     *
     * @private
     */
    onNameChange( value : string | undefined ) {
        const {
                  dataContext,
                  item
              }    = this.props,
              name = value;

        let nameError = '';

        if ( !name ) {
            nameError = __( 'YN.base.finder.item.name.error.empty' );
        }

        if ( !nameError ) {
            this.props.unsetElusiveData( this.props.uid, [ 'name', 'nameError' ] );

            if ( name !== item.name ) {
                this.props.updateItem( dataContext, item.id, { name } );
            }
        }
        else {
            this.props.setElusiveData( this.props.uid, { name, nameError } );
        }
    }

    /**
     * Handler for attribute status Dropdown change
     *
     * @private
     */
    onStatusDropdownChange( status : string ) {
        const {
                  dataContext,
                  schema,
                  item
              }        = this.props,
              statusId = metaModel[ schema ].getStatus( status )?.id;

        this.props.updateItem( dataContext, item.id, { statusId } )
    }

    /**
     * Builds the attributes form wrapped in a Card.
     *
     * @private
     */
    render() : JSX.Element {
        const {
                  uid,
                  schema,
                  status,
                  timestamp,
                  timezone,
                  item,
                  rawMode,
                  showAdditionalRawNames,
                  editable,
                  elusiveData
              }                   = this.props,
              targetArea          = getTargetArea( status, timestamp ),
              itemType            = item?.itemType,
              itemKeyDisplayName  = item ? getItemKeyDisplayName( item.itemTypeId, schema, rawMode, showAdditionalRawNames ) : '',
              itemKey             = elusiveData?.key ?? item?.key,
              itemKeyOrig         = item?.attributesModified?.get( 'key' ) as string ?? itemKey,
              itemKeyDisabled     = !editable || ( itemType?.isKeyAutogenerated ?? false ),
              itemKeyPlaceholder  = itemType?.keyDefinition ?? '',
              itemKeyError        = elusiveData?.keyError ?? '',
              itemNameDisplayName = item ? getItemNameDisplayName( item.itemTypeId, schema, rawMode, showAdditionalRawNames ) : '',
              itemName            = elusiveData?.name ?? item?.name,
              itemNameOrig        = item?.attributesModified?.get( 'name' ) as string ?? itemName,
              itemNameDisabled    = !editable,
              itemNamePlaceholder = itemType?.nameDefinition ?? '',
              itemNameError       = elusiveData?.nameError ?? '',
              itemStatus          = item?.status.name as Status,
              itemStatusOrig      = metaModel[ schema ].getStatus( item?.attributesModified?.get( 'statusId' ) as number )?.name as Status ?? itemStatus,
              itemStatusDisabled  = !editable,
              metaData            = [];

        let timestampValue,
            userIdValue;

        if ( targetArea === 'active' ) {

            // creatorUserId / creationTime
            timestampValue = timestampRenderer( item?.creationTime ?? '', timezone, rawMode );
            timestampValue = timestampValue && timestampValue !== 'now'
                ? timestampValue
                : '-';

            userIdValue = item?.creatorUserId;
            userIdValue = userIdValue
                ? userIdValue
                : '-';

            metaData.push( <div key={`${uid}_creation`} className="yn-content-block">
                <div className="yn-label">{__( 'YN.base.finder.created' )}</div>
                <div className="yn-content">
                    {timestampValue} / {userIdValue}
                </div>
            </div> );

            // modifierUserId / modificationTime
            timestampValue = timestampRenderer( item?.modificationTime ?? '', timezone, rawMode );
            timestampValue = timestampValue && timestampValue !== 'now'
                ? timestampValue
                : '-';

            userIdValue = item?.modifierUserId;
            userIdValue = userIdValue
                ? userIdValue
                : '-';

            metaData.push( <div key={`${uid}_modification`} className="yn-content-block">
                <div className="yn-label">{__( 'YN.base.finder.modified' )}</div>
                <div className="yn-content">
                    {timestampValue} / {userIdValue}
                </div>
            </div> );

            // validatorUserId / validationTime
            timestampValue = timestampRenderer( item?.validationTime ?? '', timezone, rawMode );
            timestampValue = timestampValue && timestampValue !== 'now'
                ? timestampValue
                : '-';

            userIdValue = item?.validatorUserId;
            userIdValue = userIdValue
                ? userIdValue
                : '-';

            metaData.push( <div key={`${uid}_validation`} className="yn-content-block">
                <div className="yn-label">{__( 'YN.base.finder.validated' )}</div>
                <div className="yn-content">
                    {timestampValue} / {userIdValue}
                </div>
            </div> );
        }
        else {
            // modifierUserId
            metaData.push( <div key={`${uid}_modification`} className="yn-content-block">
                <div className="yn-label">{__( 'YN.base.finder.modifiedBy' )}</div>
                <div className="yn-content">
                    {item?.modifierUserId ?? ''}
                </div>
            </div> );

            // validFromTime / validToTime
            metaData.push( <div key={`${uid}_valid`} className="yn-content-block">
                <div className="yn-label">{__( 'YN.base.finder.validFrom' )} - {__( 'YN.base.finder.validTo' )}</div>
                <div className="yn-content">
                    {timestampRenderer( item?.validFromTime ?? '', timezone, rawMode )} - {timestampRenderer( item?.validToTime ?? '', timezone, rawMode )}
                </div>
            </div> );
        }

        return <div className="fluid formgrid grid" style={{ margin: '0' }}>
            <div style={{ width: 'calc( 100% - 24rem )' }}>
                <StringField
                    uid={`${uid}_key`}
                    type="text"

                    label={itemKeyDisplayName}
                    labelAlignment="left"
                    labelPosition="top"

                    value={itemKey}
                    originalValue={itemKeyOrig}

                    onChange={this.onKeyChange}

                    autoComplete={false}
                    infoTextLayout="text"
                    disabled={itemKeyDisabled}
                    markDirty={true}
                    placeholder={itemKeyPlaceholder}
                    errorMessage={itemKeyError}

                    style={{ width: '100%' }}
                />
                <StringField
                    uid={`${uid}_name`}
                    type="text"

                    label={itemNameDisplayName}
                    labelAlignment="left"
                    labelPosition="top"

                    value={itemName}
                    originalValue={itemNameOrig}

                    onChange={this.onNameChange}

                    autoComplete={false}
                    infoTextLayout="text"
                    disabled={itemNameDisabled}
                    markDirty={true}
                    placeholder={itemNamePlaceholder}
                    errorMessage={itemNameError}

                    style={{ width: '100%' }}
                />
                <StatusDropdown
                    uid={`${uid}_status`}

                    value={itemStatus}

                    disabled={itemStatusDisabled}
                    markDirty={true}
                    originalValue={itemStatusOrig}
                    onChange={this.onStatusDropdownChange}

                    mode={itemStatusDisabled ? 'all' : 'settable'}
                    rawMode={rawMode}
                    showAdditionalRawNames={showAdditionalRawNames}
                    label={__( 'YN.base.finder.status' )}
                    style={{ width: '100%' }}
                />
            </div>
            <div style={{ width: '4rem' }}>
                <Divider layout="vertical"/>
            </div>
            <div className="field" style={{ width: '20rem' }}>
                <label className="block" style={{ marginBottom: '1rem' }}>
                    {__( 'YN.base.finder.metaData.label' )}
                </label>
                {metaData}
            </div>
        </div>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        elusiveData: undefined
    };

    // get elusiveData
    // for the data (items and references) belonging to this component
    if ( state.elusiveData?.[ ownProps.dataContext ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.dataContext ]
        };
    }
    // for all other infromation belonging to this component
    if ( state.elusiveData?.[ ownProps.uid ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.uid ]
        };
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( AttributesForm );
